import type { IUIInstanceManifest } from '../../shared/manifest/index.js';
import type { MicrofrontendNS } from '../../shared/microfrontend/index.js';
import type { IUIRuntime as IUIRuntimeBase, IAPIRuntime as IAPIRuntimeBase } from '../../shared/runtime/index.js';
import type { IUIInstance } from '../instance/index.js';

/**
 * Статус готовности Runtime
 */
export enum ERuntimeStatus {
  /**
   * Runtime зарегистрирован в Реестре, но не готов к использованию
   */
  Initial,
  /**
   * Выполняется загрузка Bundle
   */
  BundleLoading,
  /**
   * Bundle считается не готовым, если:
   * - Был не загружен хотя бы один из его ассетов
   * - Внутри Bundle, в момент исполнения JS произошла ошибка
   */
  BundleLoadFailed,
  /**
   * Bundle считается готовым к использованию, когда были загружены
   * всего Assets и была выполнена инициализация, то есть Bundle
   * сделал экспорт Runtime'ов
   */
  BundleReady,
  /**
   * Выполняется инициализация Runtime, после загрузки и инициализации
   * Bundle
   */
  BuilderLoading,
  /**
   * Не удалось инициализровать Runtime после загрузки и инициализации
   * Bundle
   */
  BuilderFailed,
  /**
   * Runtime считается готовым к использованию, когда был загружен
   * и инициализирован его Bundle, а так же был инициализирован
   * RuntimeBuilder в Bundle
   */
  Ready,
}

/**
 * Загружаемый Runtime - это такой тип райнтайма, который предвариетельно
 * необходимо загрузить и инициализировать.
 */
export interface IRuntimeLoadable {
  /**
   * Статус готовности Runtime
   */
  readonly status: ERuntimeStatus;
  /**
   * Метод обеспечивает инициализацию рантайма:
   * - Загрузка и инициализация его Bundle
   * - Инициализация RuntimeBuilder
   *
   * Этот метод необходимо использовать каждый раз, когда необходимо
   * выполнить какие-то действия с Runtime
   *
   * @returns {Promise<this>} - ссылка на сабя
   */
  ensure(): Promise<this>;
}

export interface IUIRuntime extends IUIRuntimeBase, IRuntimeLoadable {
  // TODO: сейчас это свойство используется в Web-компоненте cian-mf, для проверки наследования
  customElementConstructor?: CustomElementConstructor;

  instance(id: MicrofrontendNS.InstanceId): IUIInstance | undefined;
  instanceStrict(id: MicrofrontendNS.InstanceId): IUIInstance;

  newInstance(manifest?: Partial<IUIInstanceManifest>): IUIInstance;
}

export interface IAPIRuntime extends IAPIRuntimeBase, IRuntimeLoadable {
  call<Result>(path: string): Promise<Result>;
  call<Parameters, Result>(path: string, parameters: Parameters): Promise<Result>;
}
